import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { de } from "./de";
import { en } from "./en";

export type Language = "de" | "en";

type Translations = typeof de;

const translations: Record<Language, Translations> = { de, en };

function getInitialLanguage(): Language {
  const stored = localStorage.getItem("nexatice-lang");
  if (stored === "de" || stored === "en") return stored;

  const browserLang = navigator.language;
  if (browserLang.startsWith("de")) return "de";
  return "en";
}

interface LanguageContextValue {
  lang: Language;
  t: Translations;
  switchLanguage: (lang: Language) => void;
  getPath: (path: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Language>(getInitialLanguage);

  const legalPaths = ["/impressum", "/privacy", "/datenschutz", "/terms", "/agb"];

  useEffect(() => {
    localStorage.setItem("nexatice-lang", lang);
    document.documentElement.lang = lang;

    // Add/update hreflang alternate links for legal pages
    const currentPath = window.location.pathname.replace(/^\/(de|en)/, "") || "/";
    const isLegalPage = legalPaths.some((p) => currentPath === p || currentPath === p + "/");

    // Remove existing hreflang links
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());

    if (isLegalPage) {
      const base = window.location.origin;
      const deHref = `${base}/de${currentPath}`;
      const enHref = `${base}/en${currentPath}`;

      const deLink = document.createElement("link");
      deLink.rel = "alternate";
      deLink.hreflang = "de";
      deLink.href = deHref;
      document.head.appendChild(deLink);

      const enLink = document.createElement("link");
      enLink.rel = "alternate";
      enLink.hreflang = "en";
      enLink.href = enHref;
      document.head.appendChild(enLink);

      const xDefault = document.createElement("link");
      xDefault.rel = "alternate";
      xDefault.hreflang = "x-default";
      xDefault.href = enHref;
      document.head.appendChild(xDefault);
    }
  }, [lang]);

  const switchLanguage = useCallback((newLang: Language) => {
    const currentPath = window.location.pathname;
    // Replace /de/ or /en/ prefix
    const pathWithoutLang = currentPath.replace(/^\/(de|en)(\/|$)/, "/");
    const newPath = `/${newLang}${pathWithoutLang === "/" ? "" : pathWithoutLang}`;
    setLang(newLang);
    window.history.pushState({}, "", newPath);
    window.scrollTo(0, 0);
  }, []);

  const getPath = useCallback((path: string) => `/${lang}${path}`, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, t: translations[lang], switchLanguage, getPath }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
